import React from 'react';
import AudioControls from '../ui/AudioControls';

interface VintageLayoutProps {
    children: React.ReactNode;
}

const VintageLayout: React.FC<VintageLayoutProps> = ({ children }) => {
    return (
        <div
            className="relative min-h-screen w-full overflow-hidden transition-colors duration-700"
            style={{ backgroundColor: 'var(--bg-primary)' }}
        >
            {/* Overlay Sépia Prononcé (Variable selon thème) */}
            <div
                className="fixed inset-0 pointer-events-none z-[5]"
                style={{
                    background: 'radial-gradient(circle at center, var(--glass-border) 0%, var(--bg-secondary) 100%)',
                    mixBlendMode: 'multiply',
                    opacity: 0.4
                }}
            />

            {/* Grain Papier Vintage (Statique) */}
            <div
                className="fixed inset-0 pointer-events-none z-[6] opacity-[0.08]"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
                }}
            />

            {/* Scanlines Pellicule */}
            <div
                className="fixed inset-0 pointer-events-none z-[7]"
                style={{
                    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0, 0, 0, 0.03) 2px, rgba(0, 0, 0, 0.03) 4px)',
                    animation: 'flicker 3s infinite'
                }}
            />

            {/* Vignette Forte (Bords Brûlés) */}
            <div
                className="fixed inset-0 pointer-events-none z-[8]"
                style={{
                    background: 'radial-gradient(ellipse at center, transparent 30%, var(--bg-secondary) 70%, var(--bg-primary) 100%)',
                    boxShadow: 'inset 0 0 200px var(--bg-primary), inset 0 0 100px var(--bg-secondary)'
                }}
            />

            {/* Cadre Doré Art Déco avec Coins */}
            <div className="fixed inset-0 pointer-events-none z-[9]">
                {/* Bordure principale */}
                <div className="absolute inset-4 border-2 rounded-sm transition-colors duration-500" style={{ borderColor: 'var(--border-color)' }} />
                <div className="absolute inset-6 border transition-colors duration-500" style={{ borderColor: 'var(--glass-border)' }} />

                {/* Coins Art Déco */}
                {/* Coin Haut Gauche */}
                <svg className="absolute top-2 left-2 w-16 h-16" viewBox="0 0 100 100">
                    <path d="M 0 30 L 0 0 L 30 0" stroke="var(--accent-glow)" strokeWidth="3" fill="none" opacity="0.6" className="transition-colors duration-500" />
                    <path d="M 5 35 L 5 5 L 35 5" stroke="var(--accent-glow)" strokeWidth="1.5" fill="none" opacity="0.4" className="transition-colors duration-500" />
                    <line x1="10" y1="10" x2="25" y2="10" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                    <line x1="10" y1="10" x2="10" y2="25" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                </svg>

                {/* Coin Haut Droit */}
                <svg className="absolute top-2 right-2 w-16 h-16" viewBox="0 0 100 100">
                    <path d="M 70 0 L 100 0 L 100 30" stroke="var(--accent-glow)" strokeWidth="3" fill="none" opacity="0.6" className="transition-colors duration-500" />
                    <path d="M 65 5 L 95 5 L 95 35" stroke="var(--accent-glow)" strokeWidth="1.5" fill="none" opacity="0.4" className="transition-colors duration-500" />
                    <line x1="75" y1="10" x2="90" y2="10" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                    <line x1="90" y1="10" x2="90" y2="25" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                </svg>

                {/* Coin Bas Gauche */}
                <svg className="absolute bottom-2 left-2 w-16 h-16" viewBox="0 0 100 100">
                    <path d="M 0 70 L 0 100 L 30 100" stroke="var(--accent-glow)" strokeWidth="3" fill="none" opacity="0.6" className="transition-colors duration-500" />
                    <path d="M 5 65 L 5 95 L 35 95" stroke="var(--accent-glow)" strokeWidth="1.5" fill="none" opacity="0.4" className="transition-colors duration-500" />
                    <line x1="10" y1="90" x2="25" y2="90" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                    <line x1="10" y1="75" x2="10" y2="90" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                </svg>

                {/* Coin Bas Droit */}
                <svg className="absolute bottom-2 right-2 w-16 h-16" viewBox="0 0 100 100">
                    <path d="M 70 100 L 100 100 L 100 70" stroke="var(--accent-glow)" strokeWidth="3" fill="none" opacity="0.6" className="transition-colors duration-500" />
                    <path d="M 65 95 L 95 95 L 95 65" stroke="var(--accent-glow)" strokeWidth="1.5" fill="none" opacity="0.4" className="transition-colors duration-500" />
                    <line x1="75" y1="90" x2="90" y2="90" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                    <line x1="90" y1="75" x2="90" y2="90" stroke="var(--accent-glow)" strokeWidth="2" opacity="0.5" className="transition-colors duration-500" />
                </svg>
            </div>

            {/* Contenu */}
            <div className="relative z-10">
                {children}
            </div>

            {/* Audio Controls */}
            <div className="fixed bottom-4 left-4 z-50">
                <AudioControls />
            </div>
        </div>
    );
};

export default VintageLayout;
